import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { EMPTY, Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { ErrorService } from '../services/error.service';
import { UnauthorizedErrorHandlerRegistry } from '@core/services/error-handlers/unauthorized-error-handler.registry';
import { JWTTokenService } from '@core/services/jwt-token.service';
import { InactivityService } from '@core/services/inactivity.service';
import { isApiRequest, isLocalizationRequest } from './interceptors.helper';

const GLOBALLY_HANDLE_ERRORS = [500];

@Injectable()
export class ErrorCatchingInterceptor implements HttpInterceptor {
  constructor(
    private readonly errorService: ErrorService,
    private readonly unauthorizedErrorHandlerRegistry: UnauthorizedErrorHandlerRegistry,
    private readonly jwtService: JWTTokenService,
    private readonly inactivityService: InactivityService
  ) {}

  intercept(
    req: HttpRequest<any>,
    next: HttpHandler
  ): Observable<HttpEvent<any>> {
    return next.handle(req).pipe(
      catchError((error: HttpErrorResponse) => {
        if (this.shouldExpireSession(error, req)) {
          this.inactivityService.recordInactive(undefined, 'interceptor');
          return EMPTY;
        }

        if (error.status === 403) {
          const handler = this.unauthorizedErrorHandlerRegistry.handler;
          if (handler) {
            const handled$ = handler.handleUnauthorizedError(error, req);
            if (handled$) {
              return handled$;
            }
          }
        }

        // Unhandled internal server error
        if (GLOBALLY_HANDLE_ERRORS.includes(error.status)) {
          this.errorService.handleError(error);
        }
        return throwError(() => error);
      })
    );
  }

  private shouldExpireSession(
    error: HttpErrorResponse,
    req: HttpRequest<any>
  ): boolean {
    if (
      error.status !== 401 ||
      !this.jwtService.jwtToken ||
      !isApiRequest(req) ||
      isLocalizationRequest(req)
    ) {
      return false;
    }

    const url = req.url.toLowerCase();
    return ![
      '/api/account/login',
      '/api/account/logout',
      '/api/account/refresh-token',
    ].some((endpoint) => url.includes(endpoint));
  }
}
